// src/pages/task/views/AnchoredPopover.tsx
// Small portal popover anchored to a rect, used by the calendar for the
// "+N more" day panel and for inline quick-create. It lives in a portal
// because the calendar grid clips its own overflow, so an in-cell panel would
// be cut off on the last row and the right-hand columns.
//
// Positioning is the shared one (components/useAnchoredPosition) — this used to
// carry its own copy of the flip-and-clamp arithmetic. What stays local is the
// modal-ish shape: a full-screen dismiss layer and dialog semantics.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  hiddenUntilPositioned,
  useAnchoredPosition,
} from '@/components/useAnchoredPosition';

export interface AnchoredPopoverProps {
  /** Viewport rect of the element the panel belongs to. */
  anchor: DOMRect | null;
  onClose: () => void;
  /** Accessible name of the panel. */
  label: string;
  width?: number;
  children: ReactNode;
}

export function AnchoredPopover({ anchor, onClose, label, width = 240, children }: AnchoredPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const style = useAnchoredPosition(anchor, anchor !== null, panelRef, { width, gap: 8 });

  useEffect(() => {
    if (!anchor) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anchor, onClose]);

  if (!anchor) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onMouseDown={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={label}
        style={style ?? { ...hiddenUntilPositioned, width }}
        className="z-50 rounded-lg border border-border bg-background p-2 shadow-xl"
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
